import React, { useState, useEffect } from 'react';
import { Typography, Space, Collapse, Button, Input, Breadcrumb, Row, Col, App, Spin, Popconfirm, Upload, Image } from 'antd';
import { DownOutlined, UpOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, FileImageOutlined } from '@ant-design/icons';
import type { CollapseProps, UploadFile, UploadProps } from 'antd';
import tenantUnitService, { TenantUnit, FloorData } from '../services/tenantUnits';
import { uploadService, UploadedFile } from '../services/upload';
import { showConfirmModal } from '../components/PopupModal';
import { message } from '../utils/message';

const { Title, Text } = Typography;

const Tenants: React.FC = () => {
  const { message } = App.useApp();
  const [editingFloor, setEditingFloor] = useState<string | null>(null);
  const [editData, setEditData] = useState<TenantUnit[]>([]);
  const [floorData, setFloorData] = useState<FloorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hoverImageIndex, setHoverImageIndex] = useState<number | null>(null);
  const [activeKeys, setActiveKeys] = useState<string | string[]>([]); // Collapse activeKey 상태 관리

  // 데이터 로드
  const loadTenantUnits = async () => {
    try {
      setLoading(true);
      // console.log('=== Load Tenant Units ===');
      const data = await tenantUnitService.getTenantUnits();
      // console.log('Loaded data from API:', data);
      setFloorData(data);
      // console.log('Floor data state updated');
    } catch (error) {
      console.error('Failed to load tenant units:', error);
      message.error('층별 호수 정보를 불러오는데 실패했습니다.');

      // 초기 데이터가 없는 경우 기본 데이터 생성 제안
      if (error instanceof Error && error.message.includes('404')) {
        message.info('초기 데이터를 생성하시겠습니까?');
        try {
          await tenantUnitService.initializeDefaultData();
          message.success('초기 데이터가 생성되었습니다.');
          await loadTenantUnits(); // 다시 로드
        } catch (initError) {
          console.error('Failed to initialize data:', initError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadTenantUnits();
  }, []);

  const handleEditFloor = (floor: string, rooms: TenantUnit[]) => {
    setEditingFloor(floor);
    setEditData([...rooms]);
    // 수정 모드로 진입 시 해당 층수만 펼치고 나머지는 닫기
    setActiveKeys([floor]);
  };

  const handleSave = async () => {
    if (!editingFloor) return;

    // 보조 회사명만 입력되고 대표 회사명이 비어있는지 검증
    const invalidRooms = editData.filter(room => {
      const hasSubName = room.representativeName && room.representativeName.trim() !== '';
      const noMainName = !room.companyName || room.companyName.trim() === '';
      return hasSubName && noMainName;
    });

    if (invalidRooms.length > 0) {
      showConfirmModal({
        title: '알림',
        content: '보조 회사명만 저장할 수 없습니다.\n대표 회사명을 입력해 주세요.',
        okText: '확인',
        showCancel: false,
        onConfirm: () => {
          // 확인 버튼만 있는 알림 모달
        },
      });
      return;
    }

    try {
      setSaving(true);
      // console.log('=== Save Debug ===');
      // console.log('Saving floor:', editingFloor);
      // console.log('Edit data to save:', editData);

      const result = await tenantUnitService.updateFloorUnits(editingFloor, editData);
      // console.log('Save result:', result);

      message.success("성공적으로 저장되었습니다.");

      // 데이터 다시 로드
      // console.log('Reloading tenant units...');
      await loadTenantUnits();
      // console.log('Reload completed, new floor data:', floorData);

      setEditingFloor(null);
      setEditData([]);
    } catch (error) {
      console.error('Failed to save floor units:', error);
      message.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingFloor(null);
    setEditData([]);
  };

  const updateRoomData = (index: number, field: keyof TenantUnit, value: string) => {
    const newData = [...editData];
    newData[index] = { ...newData[index], [field]: value };
    setEditData(newData);
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = async (index: number, options: any) => {
    const { file, onSuccess, onError } = options;
    try {
      const validation = uploadService.validateFile(file as File);
      if (!validation.valid) {
        message.error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }
      const response = await uploadService.uploadSingleImage(file as File);
      if (response.success) {
        const newData = [...editData];
        newData[index] = { ...newData[index], representativeImageUrl: response.data.url };
        setEditData(newData);
        message.success('이미지가 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || '이미지 업로드에 실패했습니다.';
      message.error(errorMessage);
      onError?.(error);
    }
  };

  // 이미지 삭제 핸들러
  const handleImageDelete = (index: number) => {
    showConfirmModal({
      title: '알림',
      content: '첨부한 파일이 삭제됩니다.\n계속 진행하시겠습니까?',
      okText: '확인',
      cancelText: '취소',
      onConfirm: () => {
        const newData = [...editData];
        newData[index] = { ...newData[index], representativeImageUrl: undefined };
        setEditData(newData);
        message.success('삭제가 완료되었습니다.');
      },
    });
  };

  const addNewRoom = (floor: string) => {
    const existingRooms = editData.length > 0 ? editData :
      floorData.find(f => f.floor === floor)?.rooms || [];

    // 다음 호수 번호 계산
    const nextRoomNumber = existingRooms.length + 1;
    let newRoomNumber: string;

    if (floor.startsWith('B')) {
      // 지하층인 경우: B01, B02, B03, B04 → B01호, B02호, ...
      newRoomNumber = `B${String(nextRoomNumber).padStart(2, '0')}호`;
    } else {
      // 일반층인 경우: 13F → 1301호, 1302호, ...
      const floorNumber = floor.replace('F', '');
      newRoomNumber = `${floorNumber}${String(nextRoomNumber).padStart(2, '0')}호`;
    }

    const newRoom: TenantUnit = {
      roomNumber: newRoomNumber,
      companyName: '',
      representativeName: '',
      subName: ''
    };

    if (editingFloor === floor) {
      setEditData([...editData, newRoom]);
    } else {
      setEditingFloor(floor);
      setEditData([...existingRooms, newRoom]);
    }
  };

  const deleteRoom = async (index: number, room: TenantUnit) => {
    try {
      // 기존 호실인 경우 DB에서 삭제
      if (room.id) {
        await tenantUnitService.deleteUnit(room.id);
        message.success("삭제가 완료되었습니다.");
      }

      // 편집 중인 데이터에서 제거
      const newData = [...editData];
      newData.splice(index, 1);
      setEditData(newData);
    } catch (error) {
      console.error('Failed to delete room:', error);
      message.error('호실 삭제에 실패했습니다.');
    }
  };

  const renderRoomGrid = (rooms: TenantUnit[], floor: string) => {
    const isEditing = editingFloor === floor;
    const displayData = isEditing ? editData : rooms;

    return (
      <div style={{ padding: '16px 0' }}>
        {displayData.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <Text type="secondary">입주사 정보가 없습니다.</Text>
            {isEditing && (
              <div style={{ marginTop: '16px' }}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => addNewRoom(floor)}
                >
                  첫 번째 호수 추가
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {displayData.map((room, index) => (
              <Col key={`${room.id || index}-${room.roomNumber}`} span={12}>
                <div style={{
                  border: '1px solid #303030',
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: '#1a1a1a',
                  position: 'relative'
                }}>
                  {/* 편집 모드일 때 삭제 버튼 */}
                  {isEditing && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px'
                    }}>
                      <Popconfirm
                        title="호실 삭제"
                        description={`${room.roomNumber}을(를) 정말 삭제하시겠습니까?`}
                        onConfirm={() => deleteRoom(index, room)}
                        okText="삭제"
                        cancelText="취소"
                        okType="danger"
                      >
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          size="small"
                          danger
                          style={{
                            color: '#ff4d4f',
                            padding: '4px',
                            minWidth: '24px',
                            height: '24px'
                          }}
                        />
                      </Popconfirm>
                    </div>
                  )}

                  <Row gutter={[16, 8]} align="top">
                    {/* 호실 번호 */}
                    <Col span={isEditing ? 6 : 5}>
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {isEditing ? (
                          <Input
                            size="small"
                            value={room.roomNumber}
                            placeholder="호실 번호를 입력하세요"
                            style={{ fontSize: '12px', fontWeight: 'bold' }}
                            onChange={(e) => updateRoomData(index, 'roomNumber', e.target.value)}
                          />
                        ) : (
                          <Text strong style={{ fontSize: '16px' }}>{room.roomNumber}</Text>
                        )}
                      </div>
                    </Col>

                    {/* 대표 이미지 - 편집 모드일 때 */}
                    {isEditing && (
                      <Col span={4}>
                        <div>
                          <Text style={{ fontSize: '12px', color: '#a6a6a6', marginBottom: '4px', display: 'block' }}>
                            대표 이미지
                          </Text>
                          {room.representativeImageUrl ? (
                            <div
                              style={{
                                position: 'relative',
                                width: '60px',
                                height: '60px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                backgroundImage: 'linear-gradient(45deg, #e6e6e6 25%, transparent 25%, transparent 75%, #e6e6e6 75%, #e6e6e6), linear-gradient(45deg, #e6e6e6 25%, transparent 25%, transparent 75%, #e6e6e6 75%, #e6e6e6)',
                                backgroundSize: '20px 20px',
                                backgroundPosition: '0 0, 10px 10px',
                                backgroundColor: '#ffffff'
                              }}
                              onMouseEnter={() => setHoverImageIndex(index)}
                              onMouseLeave={() => setHoverImageIndex(null)}
                            >
                              <img
                                src={room.representativeImageUrl}
                                alt="대표 이미지"
                                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }}
                              />
                              {hoverImageIndex === index && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px'
                                  }}
                                  onClick={() => handleImageDelete(index)}
                                >
                                  <DeleteOutlined style={{ fontSize: '20px', color: '#fff' }} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <Upload
                              customRequest={(options) => handleImageUpload(index, options)}
                              showUploadList={false}
                              accept="image/*"
                            >
                              <div style={{
                                width: '60px',
                                height: '60px',
                                border: '1px dashed #303030',
                                borderRadius: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                backgroundColor: '#0a0a0a'
                              }}>
                                <PlusOutlined style={{ fontSize: '16px', color: '#666666' }} />
                                <Text style={{ fontSize: '10px', color: '#666666' }}>추가</Text>
                              </div>
                            </Upload>
                          )}
                        </div>
                      </Col>
                    )}

                    {/* 상세보기 모드일 때: 대표 이미지를 호실번호 오른쪽에 배치 */}
                    {!isEditing && (
                      <Col span={4}>
                        <div>
                          <Text style={{ fontSize: '12px', color: '#a6a6a6', marginBottom: '4px', display: 'block' }}>
                            대표 이미지
                          </Text>
                          {room.representativeImageUrl ? (
                            <div style={{
                              borderRadius: '4px',
                              width: '60px',
                              height: '60px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundImage: 'linear-gradient(45deg, #e6e6e6 25%, transparent 25%, transparent 75%, #e6e6e6 75%, #e6e6e6), linear-gradient(45deg, #e6e6e6 25%, transparent 25%, transparent 75%, #e6e6e6 75%, #e6e6e6)',
                              backgroundSize: '20px 20px',
                              backgroundPosition: '0 0, 10px 10px',
                              backgroundColor: '#ffffff'
                            }}>
                              <Image
                                width={60}
                                height={60}
                                src={room.representativeImageUrl}
                                alt="대표 이미지"
                                style={{ objectFit: 'contain', borderRadius: '4px' }}
                              />
                            </div>
                          ) : (
                            <div style={{
                              width: '60px',
                              height: '60px',
                              border: '1px dashed #303030',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#0a0a0a'
                            }}>
                              <Text style={{ fontSize: '9px', color: '#666666' }}>No Image</Text>
                            </div>
                          )}
                        </div>
                      </Col>
                    )}

                    {/* 회사명 (대표 + 보조) */}
                    <Col span={isEditing ? 12 : 12}>
                      <div>
                        {/* 대표 회사명 */}
                        <div style={{ marginBottom: '8px' }}>
                          <Text style={{ fontSize: '12px', color: '#a6a6a6', marginBottom: '4px', display: 'block' }}>
                            대표 회사명
                          </Text>
                          {isEditing ? (
                            <Input
                              size="small"
                              value={room.companyName}
                              placeholder="대표 회사명을 입력하세요"
                              style={{ fontSize: '12px' }}
                              onChange={(e) => updateRoomData(index, 'companyName', e.target.value)}
                            />
                          ) : (
                            <Text style={{
                              fontSize: '12px',
                              color: room.companyName && room.companyName.trim() !== '' ? '#ffffff' : '#666666'
                            }}>
                              {room.companyName && room.companyName.trim() !== '' ? room.companyName : '-'}
                            </Text>
                          )}
                        </div>

                        {/* 보조 회사명 */}
                        <div>
                          <Text style={{ fontSize: '12px', color: '#a6a6a6', marginBottom: '4px', display: 'block' }}>
                            보조 회사명
                          </Text>
                          {isEditing ? (
                            <Input
                              size="small"
                              value={room.representativeName}
                              placeholder="보조 회사명을 입력하세요"
                              style={{ fontSize: '12px' }}
                              onChange={(e) => updateRoomData(index, 'representativeName', e.target.value)}
                            />
                          ) : (
                            <Text style={{
                              fontSize: '12px',
                              color: room.representativeName && room.representativeName.trim() !== '' ? '#ffffff' : '#666666'
                            }}>
                              {room.representativeName && room.representativeName.trim() !== '' ? room.representativeName : '-'}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col span={isEditing ? 2 : 3}>
                      {/* 삭제 버튼을 위한 공간 확보 */}
                    </Col>
                  </Row>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* 호수 추가 버튼 */}
        {isEditing && displayData.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addNewRoom(floor)}
              size="small"
            >
              호수 추가
            </Button>
          </div>
        )}

        {/* 하단 버튼 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '16px',
          gap: '8px'
        }}>
          {isEditing ? (
            <>
              <Button onClick={handleCancel} disabled={saving}>
                취소
              </Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                저장
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              onClick={() => handleEditFloor(floor, rooms)}
            >
              수정
            </Button>
          )}
        </div>
      </div>
    );
  };

  const customExpandIcon = ({ isActive }: { isActive?: boolean }) => {
    return isActive ? <UpOutlined /> : <DownOutlined />;
  };

  // 로딩 중일 때 스피너 표시
  if (loading) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%', minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
        <Text>층별 호수 정보를 불러오는 중...</Text>
      </Space>
    );
  }

  const collapseItems: CollapseProps['items'] = floorData.map((floor) => {
    const isDisabled = editingFloor && editingFloor !== floor.floor;

    return {
      key: floor.floor,
      label: (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '16px',
          opacity: isDisabled ? 0.4 : 1
        }}>
          <Text strong style={{ fontSize: '18px' }}>{floor.floor}</Text>
          <div style={{ display: 'flex', gap: '24px' }}>
            <Text>입주사 : {floor.tenantCount}개</Text>
            <Text>공실 : {floor.emptyCount}개</Text>
          </div>
        </div>
      ),
      children: renderRoomGrid(floor.rooms, floor.floor),
      collapsible: isDisabled ? 'disabled' : undefined,
      style: {
        marginBottom: '1px',
        backgroundColor: '#1f1f1f',
        border: '1px solid #303030'
      }
    };
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '건물 안내' },
          { title: '입주사 관리' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        입주사 관리
      </Title>

      {/* 층별 아코디언 */}
      <Collapse
        items={collapseItems}
        expandIcon={customExpandIcon}
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        style={{
          backgroundColor: 'transparent',
          border: 'none'
        }}
      />
    </Space>
  );
};

export default Tenants;